import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api';

type ExpenseArticle = {
  id: string;
  name: string;
  code?: string;
  number?: string;
};

type SkuFinanceRow = {
  productId: number;
  sku: string;
  name: string;
  quantity: number;
  incomeRub: number;
  incomePoints: number;
  totalIncome: number;
  averagePrice: number;
  expenses: Record<string, number>;
  totalExpense: number;
  averageExpense: number;
  expenseShare: number;
  taxes: number;
  purchaseCost: number;
  revenue: number;
  margin: number;
};

function formatNumber(value: number, digits?: number) {
  const hasFraction = Math.abs(value % 1) > 0;
  const fractionDigits = digits ?? (hasFraction ? 2 : 0);
  const factor = 10 ** fractionDigits;
  const rounded = fractionDigits ? Math.round(value * factor) / factor : Math.round(value);
  return rounded
    .toLocaleString('ru-RU', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })
    .replace(/\s/g, '\u00A0');
}

const SkuFinanceTable: React.FC = () => {
  const [rows, setRows] = useState<SkuFinanceRow[]>([]);
  const [expenseArticles, setExpenseArticles] = useState<ExpenseArticle[]>([]);
  const [taxRate, setTaxRate] = useState<number | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'all'>('all');
  const [loading, setLoading] = useState(false);
  const [activeSku, setActiveSku] = useState<string | null>(null);
  const storageKey = 'sku_finance_period';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(storageKey);
    if (stored === 'day' || stored === 'week' || stored === 'month' || stored === 'all') {
      setPeriod(stored);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, period);
  }, [period]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await apiFetch(`/api/dashboard/sku-finance?period=${period}`);
        if (res.ok) {
          const json = await res.json();
          setRows(json.rows || []);
          setExpenseArticles(json.expenseArticles || []);
          setTaxRate(typeof json.taxRate === 'number' ? json.taxRate : null);
        }
      } finally {
        setLoading(false);
      }
    }
    load().catch(console.error);
  }, [period]);

  const totals = useMemo(() => {
    const base = {
      quantity: 0,
      incomeRub: 0,
      incomePoints: 0,
      totalIncome: 0,
      totalExpense: 0,
      taxes: 0,
      purchaseCost: 0,
      revenue: 0,
      expenses: {} as Record<string, number>,
    };
    rows.forEach(r => {
      base.quantity += r.quantity;
      base.incomeRub += r.incomeRub;
      base.incomePoints += r.incomePoints;
      base.totalIncome += r.totalIncome;
      base.totalExpense += r.totalExpense;
      base.taxes += r.taxes;
      base.purchaseCost += r.purchaseCost;
      base.revenue += r.revenue;
      expenseArticles.forEach(a => {
        base.expenses[a.id] = (base.expenses[a.id] || 0) + (r.expenses[a.id] || 0);
      });
    });
    return base;
  }, [rows, expenseArticles]);

  if (!rows.length) return null;

  return (
    <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow p-4 mb-6 mt-8">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Экономика по артикулам товара</h2>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {[
            { key: 'day', label: 'День' },
            { key: 'week', label: 'Неделя' },
            { key: 'month', label: 'Месяц' },
            { key: 'all', label: 'Всего' },
          ].map(btn => (
            <button
              key={btn.key}
              className={`px-3 py-1.5 rounded-full border text-sm ${
                period === btn.key
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-600 border-gray-200'
              }`}
              onClick={() => setPeriod(btn.key as any)}
            >
              {btn.label}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-auto max-h-[70vh] rounded-xl border border-gray-200 relative">
        {loading && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
            <div className="animate-pulse text-sm text-gray-500">Загрузка отчёта…</div>
          </div>
        )}
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 sticky top-0 z-20">
            <tr>
              <th className="px-2 py-2 text-left sticky left-0 z-20 bg-gray-50 hidden sm:table-cell w-32 min-w-[8rem]">Артикул товара</th>
              <th className="px-2 py-2 text-left sticky left-0 sm:left-32 z-20 bg-gray-50">Товар</th>
              <th className="px-2 py-2 text-center">Кол-во</th>
              <th className="px-2 py-2 text-center">Приход ₽</th>
              <th className="px-2 py-2 text-center">Приход баллы</th>
              <th className="px-2 py-2 text-center">Итого приход</th>
              <th className="px-2 py-2 text-center">Средняя цена</th>
              {expenseArticles.map(a => (
                <th key={a.id} className="px-2 py-2 text-center">{a.code || a.name}</th>
              ))}
              <th className="px-2 py-2 text-center">Итого расход</th>
              <th className="px-2 py-2 text-center">Расход за шт.</th>
              <th className="px-2 py-2 text-center">% от цены</th>
              <th className="px-2 py-2 text-center">Налог {taxRate ?? 7}%</th>
              <th className="px-2 py-2 text-center">Выручка</th>
              <th className="px-2 py-2 text-center">Маржа</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(row => (
              <tr
                key={row.productId}
                className="odd:bg-gray-50 relative"
                onClick={() => {
                  if (window.innerWidth < 640) {
                    setActiveSku(prev => (prev === row.sku ? null : row.sku));
                  }
                }}
              >
                <td className="px-2 py-1 whitespace-nowrap sticky left-0 z-10 bg-white hidden sm:table-cell w-32 min-w-[8rem]">{row.sku}</td>
                <td className="px-2 py-1 whitespace-nowrap sticky left-0 sm:left-32 z-10 bg-white">
                  {row.name}
                  {activeSku === row.sku && (
                    <span className="ml-2 inline-flex sm:hidden items-center rounded-full bg-slate-900 text-white text-xs px-2 py-0.5">
                      Артикул товара: {row.sku}
                    </span>
                  )}
                </td>
              <td className="px-2 py-1 text-center">{formatNumber(row.quantity)}</td>
                <td className="px-2 py-1 text-center">{formatNumber(row.incomeRub)}</td>
                <td className="px-2 py-1 text-center">{formatNumber(row.incomePoints)}</td>
                <td className="px-2 py-1 text-center font-semibold">{formatNumber(row.totalIncome)}</td>
                <td className="px-2 py-1 text-center">{formatNumber(row.averagePrice, 2)}</td>
                {expenseArticles.map(a => (
                  <td key={`${row.productId}-${a.id}`} className="px-2 py-1 text-center">
                    {row.expenses[a.id] ? formatNumber(row.expenses[a.id]) : '—'}
                  </td>
                ))}
                <td className="px-2 py-1 text-center font-semibold">{formatNumber(row.totalExpense)}</td>
                <td className="px-2 py-1 text-center">{formatNumber(row.averageExpense, 2)}</td>
                <td className="px-2 py-1 text-center">{formatNumber(row.expenseShare, 2)}%</td>
                <td className="px-2 py-1 text-center text-red-600">{formatNumber(row.taxes)}</td>
                <td className="px-2 py-1 text-center font-semibold">{formatNumber(row.revenue)}</td>
                <td className="px-2 py-1 text-center">{formatNumber(row.margin * 100, 2)}%</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 font-semibold">
            <tr>
              <td className="px-2 py-2 sticky left-0 z-10 bg-gray-50 hidden sm:table-cell">Итого</td>
              <td className="px-2 py-2 sticky left-0 sm:left-32 z-10 bg-gray-50">Итого</td>
              <td className="px-2 py-2 text-center">{formatNumber(totals.quantity)}</td>
              <td className="px-2 py-2 text-center">{formatNumber(totals.incomeRub)}</td>
              <td className="px-2 py-2 text-center">{formatNumber(totals.incomePoints)}</td>
              <td className="px-2 py-2 text-center">{formatNumber(totals.totalIncome)}</td>
              <td className="px-2 py-2" />
              {expenseArticles.map(a => (
                <td key={`t-${a.id}`} className="px-2 py-2 text-center">
                  {totals.expenses[a.id] ? formatNumber(totals.expenses[a.id]) : '—'}
                </td>
              ))}
              <td className="px-2 py-2 text-center">{formatNumber(totals.totalExpense)}</td>
              <td className="px-2 py-2" />
              <td className="px-2 py-2" />
              <td className="px-2 py-2 text-center text-red-600">{formatNumber(totals.taxes)}</td>
              <td className="px-2 py-2 text-center">{formatNumber(totals.revenue)}</td>
              <td className="px-2 py-2 text-center">
                {totals.purchaseCost
                  ? `${formatNumber((totals.revenue / totals.purchaseCost) * 100, 2)}%`
                  : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default SkuFinanceTable;
